import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Loader2, Youtube, Instagram, Zap, Target, Clock, TrendingUp,
  BarChart3, Brain, RefreshCw, ChevronDown, ChevronUp
} from "lucide-react";

const DAY_NAMES = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

export const LearnAlgoEngine = ({ channels, API }) => {
  const [selectedChannel, setSelectedChannel] = useState(null);
  const [engineData, setEngineData] = useState(null);
  const [prediction, setPrediction] = useState(null);
  const [competitors, setCompetitors] = useState(null);
  const [collecting, setCollecting] = useState(false);
  const [predicting, setPredicting] = useState(false);
  const [loadingComp, setLoadingComp] = useState(false);

  // Prediction form
  const [predTitle, setPredTitle] = useState("");
  const [predHour, setPredHour] = useState(18);
  const [predDay, setPredDay] = useState(3);
  const [predIsShort, setPredIsShort] = useState(false);
  const [predMediaType, setPredMediaType] = useState("VIDEO");

  // Collapsible sections
  const [showCorrelations, setShowCorrelations] = useState(false);
  const [showPatterns, setShowPatterns] = useState(false);

  const collectData = async (channelId) => {
    setCollecting(true);
    setEngineData(null);
    setPrediction(null);
    try {
      const res = await axios.post(`${API}/algo-engine/collect/${channelId}`);
      setEngineData(res.data);
      toast.success("DATA COLLECTED & MODEL TRAINED", {
        description: `${res.data.videos_collected} videos analyzed`
      });
    } catch (e) {
      toast.error("Collection failed", { description: e.response?.data?.detail || e.message });
    }
    setCollecting(false);
  };

  const runPrediction = async () => {
    if (!selectedChannel || !predTitle.trim()) {
      toast.error("Select a channel and enter a title");
      return;
    }
    setPredicting(true);
    setPrediction(null);
    try {
      const params = new URLSearchParams({
        channel_id: selectedChannel.id,
        title: predTitle,
        upload_hour: predHour,
        upload_day: predDay,
        is_short: predIsShort,
        media_type: predMediaType,
      });
      const res = await axios.post(`${API}/algo-engine/predict?${params}`);
      setPrediction(res.data);
      toast.success(`PREDICTION: ${res.data.score}/100 (${res.data.rating})`);
    } catch (e) {
      toast.error("Prediction failed", { description: e.response?.data?.detail || e.message });
    }
    setPredicting(false);
  };

  const discoverCompetitors = async () => {
    if (!selectedChannel) return;
    setLoadingComp(true);
    try {
      const res = await axios.get(`${API}/algo-engine/competitors/${selectedChannel.id}`);
      setCompetitors(res.data);
      toast.success(`Found ${res.data.competitors?.length || 0} competitors`);
    } catch (e) {
      toast.error("Discovery failed", { description: e.response?.data?.detail || e.message });
    }
    setLoadingComp(false);
  };

  const getScoreColor = (score) => {
    if (score >= 80) return "text-green-400";
    if (score >= 60) return "text-cyan-400";
    if (score >= 40) return "text-yellow-400";
    return "text-red-400";
  };

  const getScoreBorder = (score) => {
    if (score >= 80) return "border-green-500";
    if (score >= 60) return "border-cyan-500";
    if (score >= 40) return "border-yellow-500";
    return "border-red-500";
  };

  const getCorrColor = (val) => {
    if (val > 0.3) return "text-green-400";
    if (val > 0) return "text-cyan-400";
    if (val > -0.3) return "text-yellow-400";
    return "text-red-400";
  };

  return (
    <div data-testid="learnalgo-view" className="max-w-5xl">
      <h2 className="font-display font-bold text-xl tracking-wider text-white mb-1">
        ALGORITHM LEARNING ENGINE
      </h2>
      <p className="text-zinc-500 text-sm mb-6">
        Collects your data, builds a scoring model, and predicts how the algorithm will respond to new content.
      </p>

      {/* Step 1: Select Channel */}
      <div className="widget mb-6">
        <div className="widget-header">
          <span className="widget-title">
            <Target className="w-4 h-4 inline mr-2 text-cyan-400" />
            STEP 1: SELECT CHANNEL & TRAIN MODEL
          </span>
        </div>
        <div className="widget-content">
          <div className="flex flex-wrap gap-2 mb-4">
            {channels.map((ch) => (
              <button
                key={ch.id}
                onClick={() => { setSelectedChannel(ch); setEngineData(null); setPrediction(null); setCompetitors(null); }}
                data-testid={`engine-channel-${ch.id}`}
                className={`px-4 py-2 text-xs border transition-all flex items-center gap-2 ${
                  selectedChannel?.id === ch.id
                    ? "border-cyan-500 bg-cyan-500/20 text-cyan-300"
                    : "border-zinc-700 text-zinc-400 hover:border-zinc-500"
                }`}
              >
                {ch.platform === "instagram" ? <Instagram className="w-3 h-3" /> : <Youtube className="w-3 h-3" />}
                {ch.handle || ch.name}
              </button>
            ))}
          </div>

          {selectedChannel && (
            <button
              onClick={() => collectData(selectedChannel.id)}
              disabled={collecting}
              data-testid="collect-data-btn"
              className="px-6 py-2.5 text-xs font-bold bg-gradient-to-r from-cyan-600 to-blue-600 text-white hover:from-cyan-500 hover:to-blue-500 transition-all disabled:opacity-50 flex items-center gap-2"
            >
              {collecting ? <><Loader2 className="w-4 h-4 animate-spin" /> COLLECTING & TRAINING...</> :
                <><Zap className="w-4 h-4" /> COLLECT DATA & TRAIN MODEL</>}
            </button>
          )}
        </div>
      </div>

      {/* Model Results */}
      {engineData && (
        <div className="widget mb-6">
          <div className="widget-header">
            <span className="widget-title">
              <Brain className="w-4 h-4 inline mr-2 text-green-400" />
              MODEL TRAINED
            </span>
            <span className="text-xs text-green-400 border border-green-500/30 px-2 py-0.5">
              {engineData.videos_collected} VIDEOS
            </span>
          </div>
          <div className="widget-content">
            {/* Key Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
              <div className="p-3 bg-zinc-900 border border-zinc-800 text-center">
                <p className="text-xs text-zinc-500 mb-1">AVG SCORE</p>
                <p className={`text-2xl font-bold ${getScoreColor(engineData.model_summary?.avg_performance || 0)}`}>
                  {engineData.model_summary?.avg_performance}
                </p>
              </div>
              <div className="p-3 bg-zinc-900 border border-zinc-800 text-center">
                <p className="text-xs text-zinc-500 mb-1">BEST DAY</p>
                <p className="text-lg font-bold text-cyan-400">
                  {DAY_NAMES[engineData.model_summary?.best_upload_day?.value] || "—"}
                </p>
                <p className="text-[10px] text-zinc-600">score {engineData.model_summary?.best_upload_day?.avg_score}</p>
              </div>
              <div className="p-3 bg-zinc-900 border border-zinc-800 text-center">
                <p className="text-xs text-zinc-500 mb-1">BEST HOUR</p>
                <p className="text-lg font-bold text-purple-400">
                  {engineData.model_summary?.best_upload_hour?.value || "—"}:00 UTC
                </p>
                <p className="text-[10px] text-zinc-600">score {engineData.model_summary?.best_upload_hour?.avg_score}</p>
              </div>
              <div className="p-3 bg-zinc-900 border border-zinc-800 text-center">
                <p className="text-xs text-zinc-500 mb-1">TOP SIGNAL</p>
                <p className="text-sm font-bold text-yellow-400 break-all">
                  {engineData.model_summary?.top_features?.[0] || "—"}
                </p>
              </div>
            </div>

            {/* Feature Correlations */}
            <button onClick={() => setShowCorrelations(!showCorrelations)}
              className="flex items-center gap-2 text-xs text-zinc-400 hover:text-white mb-2 w-full text-left"
            >
              {showCorrelations ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
              FEATURE CORRELATIONS WITH ALGORITHM BOOST ({Object.keys(engineData.feature_correlations || {}).length} features)
            </button>
            {showCorrelations && (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mb-4">
                {Object.entries(engineData.feature_correlations || {}).map(([feat, corr]) => (
                  <div key={feat} className="flex items-center justify-between p-2 bg-zinc-900 border border-zinc-800">
                    <span className="text-xs text-zinc-400 truncate mr-2">{feat.replace(/_/g, " ")}</span>
                    <span className={`text-xs font-bold ${getCorrColor(corr)}`}>{corr > 0 ? "+" : ""}{corr}</span>
                  </div>
                ))}
              </div>
            )}

            {/* Patterns */}
            <button onClick={() => setShowPatterns(!showPatterns)}
              className="flex items-center gap-2 text-xs text-zinc-400 hover:text-white mb-2 w-full text-left"
            >
              {showPatterns ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
              TOP vs BOTTOM PERFORMER PATTERNS
            </button>
            {showPatterns && engineData.patterns?.top_performers && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
                <div className="border border-green-500/20 p-3">
                  <p className="text-xs text-green-400 font-semibold mb-2">TOP 20% AVERAGES</p>
                  {Object.entries(engineData.patterns.top_performers).slice(0, 10).map(([k, v]) => (
                    <div key={k} className="flex justify-between text-[11px] py-0.5">
                      <span className="text-zinc-500">{k.replace(/_/g, " ")}</span>
                      <span className="text-green-300">{typeof v === "number" ? v.toFixed(2) : v}</span>
                    </div>
                  ))}
                </div>
                <div className="border border-red-500/20 p-3">
                  <p className="text-xs text-red-400 font-semibold mb-2">BOTTOM 20% AVERAGES</p>
                  {Object.entries(engineData.patterns.underperformers || {}).slice(0, 10).map(([k, v]) => (
                    <div key={k} className="flex justify-between text-[11px] py-0.5">
                      <span className="text-zinc-500">{k.replace(/_/g, " ")}</span>
                      <span className="text-red-300">{typeof v === "number" ? v.toFixed(2) : v}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Top Videos */}
            {engineData.model_summary?.top_videos?.length > 0 && (
              <div className="mt-2">
                <p className="text-xs text-zinc-500 mb-2">TOP PERFORMING CONTENT (model training data)</p>
                {engineData.model_summary.top_videos.map((v, i) => (
                  <div key={i} className="flex items-center gap-3 p-2 bg-zinc-900 border border-zinc-800 mb-1">
                    <span className={`text-xs font-bold ${getScoreColor(v.score)} w-10 text-right`}>{v.score}</span>
                    <span className="text-xs text-zinc-400 truncate flex-1">{v.title}</span>
                    <span className="text-[10px] text-zinc-600">{v.views?.toLocaleString()} views</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Step 2: Predict */}
      {engineData && (
        <div className="widget mb-6">
          <div className="widget-header">
            <span className="widget-title">
              <TrendingUp className="w-4 h-4 inline mr-2 text-yellow-400" />
              STEP 2: PREDICT NEW CONTENT PERFORMANCE
            </span>
          </div>
          <div className="widget-content">
            <div className="space-y-3">
              <div>
                <label className="text-xs text-zinc-500 mb-1 block">TITLE / CAPTION</label>
                <input
                  type="text"
                  value={predTitle}
                  onChange={(e) => setPredTitle(e.target.value)}
                  placeholder={selectedChannel?.platform === "instagram" ? "Your Instagram caption..." : "Your YouTube video title..."}
                  className="w-full bg-zinc-900 border border-zinc-800 text-white px-3 py-2.5 text-sm focus:outline-none focus:border-cyan-500"
                  data-testid="predict-title-input"
                />
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div>
                  <label className="text-xs text-zinc-500 mb-1 block">UPLOAD DAY</label>
                  <select value={predDay} onChange={(e) => setPredDay(Number(e.target.value))}
                    className="w-full bg-zinc-900 border border-zinc-800 text-white px-2 py-2 text-xs">
                    {DAY_NAMES.map((d, i) => <option key={i} value={i}>{d}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs text-zinc-500 mb-1 block">UPLOAD HOUR (UTC)</label>
                  <select value={predHour} onChange={(e) => setPredHour(Number(e.target.value))}
                    className="w-full bg-zinc-900 border border-zinc-800 text-white px-2 py-2 text-xs">
                    {Array.from({ length: 24 }, (_, i) => <option key={i} value={i}>{i}:00</option>)}
                  </select>
                </div>
                {selectedChannel?.platform === "youtube" && (
                  <div>
                    <label className="text-xs text-zinc-500 mb-1 block">FORMAT</label>
                    <select value={predIsShort ? "short" : "long"} onChange={(e) => setPredIsShort(e.target.value === "short")}
                      className="w-full bg-zinc-900 border border-zinc-800 text-white px-2 py-2 text-xs">
                      <option value="long">Standard Video</option>
                      <option value="short">Short (&lt;60s)</option>
                    </select>
                  </div>
                )}
                {selectedChannel?.platform === "instagram" && (
                  <div>
                    <label className="text-xs text-zinc-500 mb-1 block">MEDIA TYPE</label>
                    <select value={predMediaType} onChange={(e) => setPredMediaType(e.target.value)}
                      className="w-full bg-zinc-900 border border-zinc-800 text-white px-2 py-2 text-xs">
                      <option value="VIDEO">Reel / Video</option>
                      <option value="IMAGE">Image Post</option>
                      <option value="CAROUSEL_ALBUM">Carousel</option>
                    </select>
                  </div>
                )}
              </div>
              <button
                onClick={runPrediction}
                disabled={predicting || !predTitle.trim()}
                data-testid="predict-btn"
                className="px-6 py-2.5 text-xs font-bold bg-gradient-to-r from-yellow-600 to-orange-600 text-black hover:from-yellow-500 hover:to-orange-500 transition-all disabled:opacity-50 flex items-center gap-2"
              >
                {predicting ? <><Loader2 className="w-4 h-4 animate-spin" /> PREDICTING...</> :
                  <><Zap className="w-4 h-4" /> PREDICT ALGORITHM RESPONSE</>}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Prediction Result */}
      {prediction && (
        <div className="widget mb-6" data-testid="prediction-result">
          <div className="widget-header">
            <span className="widget-title">ALGORITHM PREDICTION</span>
            <span className={`text-xs border px-2 py-0.5 font-bold ${getScoreBorder(prediction.score)} ${getScoreColor(prediction.score)}`}>
              {prediction.rating}
            </span>
          </div>
          <div className="widget-content">
            {/* Score Display */}
            <div className="flex items-center gap-6 mb-6">
              <div className={`w-24 h-24 flex items-center justify-center border-4 ${getScoreBorder(prediction.score)}`}>
                <span className={`text-3xl font-bold ${getScoreColor(prediction.score)}`}>{prediction.score}</span>
              </div>
              <div>
                <p className="text-sm text-white mb-1">Algorithm Response Score: <span className={`font-bold ${getScoreColor(prediction.score)}`}>{prediction.score}/100</span></p>
                <p className="text-xs text-zinc-500">Best upload: {prediction.best_upload_day} at {prediction.best_upload_hour_utc}:00 UTC</p>
                <p className="text-xs text-zinc-600">Model trained on {prediction.model_strength} videos</p>
              </div>
            </div>

            {/* Feature Breakdown */}
            {prediction.breakdown && Object.keys(prediction.breakdown).length > 0 && (
              <div className="mb-4">
                <p className="text-xs text-zinc-500 mb-2">FEATURE BREAKDOWN</p>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                  {Object.entries(prediction.breakdown)
                    .sort((a, b) => Math.abs(b[1].weight) - Math.abs(a[1].weight))
                    .slice(0, 9)
                    .map(([feat, data]) => (
                    <div key={feat} className="p-2 bg-zinc-900 border border-zinc-800">
                      <p className="text-[10px] text-zinc-500">{feat.replace(/_/g, " ").toUpperCase()}</p>
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-white">{data.value}</span>
                        <span className="text-[10px] text-zinc-600">ideal: {data.ideal}</span>
                      </div>
                      <div className="w-full h-1 bg-zinc-800 mt-1">
                        <div className={`h-full ${data.contribution > 0 ? "bg-green-500" : "bg-red-500"}`}
                          style={{ width: `${Math.min(Math.abs(data.contribution) * 200, 100)}%` }} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* AI Suggestions */}
            {prediction.suggestions && (
              <div className="border-t border-zinc-800 pt-4 mt-4">
                <p className="text-xs text-yellow-400 font-semibold mb-2">AI OPTIMIZATION SUGGESTIONS</p>
                <div className="ai-output text-sm whitespace-pre-wrap max-h-[400px] overflow-y-auto leading-relaxed">
                  {prediction.suggestions}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Step 3: Competitors */}
      {engineData && selectedChannel?.platform === "youtube" && (
        <div className="widget mb-6">
          <div className="widget-header">
            <span className="widget-title">
              <BarChart3 className="w-4 h-4 inline mr-2 text-pink-400" />
              STEP 3: COMPETITOR DISCOVERY
            </span>
          </div>
          <div className="widget-content">
            <button
              onClick={discoverCompetitors}
              disabled={loadingComp}
              data-testid="discover-competitors-btn"
              className="px-4 py-2 text-xs border border-pink-500/50 text-pink-400 hover:bg-pink-500/10 transition-all disabled:opacity-50 flex items-center gap-2 mb-4"
            >
              {loadingComp ? <><Loader2 className="w-3 h-3 animate-spin" /> DISCOVERING...</> :
                <><RefreshCw className="w-3 h-3" /> AUTO-DISCOVER COMPETITORS IN YOUR NICHE</>}
            </button>

            {competitors?.competitors?.length > 0 && (
              <div className="space-y-2">
                <p className="text-xs text-zinc-500">Found {competitors.competitors.length} channels (keywords: {competitors.query_keywords?.join(", ")})</p>
                {competitors.competitors.map((comp, i) => (
                  <div key={i} className="flex items-center gap-3 p-2 bg-zinc-900 border border-zinc-800">
                    {comp.thumbnail && <img src={comp.thumbnail} alt="" className="w-8 h-8 rounded-full" />}
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-white truncate">{comp.title}</p>
                      <p className="text-[10px] text-zinc-600 truncate">{comp.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
